
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function EdgemeadPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Edgemead Painters - Professional Painting Services Cape Town";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Edgemead family homes and residential properties. Expert interior, exterior, and maintenance painting solutions. Get your free quote today.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Edgemead painters, family home painting, residential painting, suburban painting, home maintenance, Cape Town painters');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/edgemead`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Edgemead Painters - Professional Painting Services",
      "description": "Professional painting services for Edgemead family homes and residential properties. Expert interior, exterior, and maintenance painting solutions.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/edgemead`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Edgemead",
        "description": "Family-focused painting services for Edgemead's residential community and suburban properties.",
        "areaServed": "Edgemead, Cape Town",
        "serviceType": "Residential Family Home Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      // Cleanup
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Edgemead')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-heart-line',
      title: 'Family Home Painting',
      description: 'Complete interior and exterior painting services for Edgemead family homes with child-safe materials and family-friendly scheduling.',
      price: 'From R10,000',
      features: ['Child-safe paints', 'Family scheduling', 'Minimal disruption', 'Quality finishes']
    },
    {
      icon: 'ri-brush-line',
      title: 'Interior Room Makeovers',
      description: 'Transform individual rooms in your Edgemead home with modern color schemes and professional techniques for bedrooms, living areas, and kitchens.',
      price: 'From R3,500',
      features: ['Room-by-room service', 'Modern colors', 'Quick turnaround', 'Furniture protection']
    },
    {
      icon: 'ri-building-line',
      title: 'Exterior House Painting',
      description: 'Comprehensive exterior painting services for Edgemead homes including weather protection and curb appeal enhancement.',
      price: 'From R12,000',
      features: ['Weather protection', 'Curb appeal', 'Durable finishes', 'Color consultation']
    },
    {
      icon: 'ri-tools-line',
      title: 'Home Maintenance Painting',
      description: 'Regular maintenance painting services for Edgemead homeowners including touch-ups, repairs, and preventive treatments.',
      price: 'From R2,500',
      features: ['Regular maintenance', 'Touch-up services', 'Preventive care', 'Affordable rates']
    },
    {
      icon: 'ri-palette-line',
      title: 'Color Design Consultation',
      description: 'Professional color consultation services to help Edgemead families choose the perfect colors for their home environment.',
      price: 'From R1,500',
      features: ['Family-friendly colors', 'Design advice', 'Color matching', 'Trend guidance']
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Protective Coatings',
      description: 'Specialized protective coatings for Edgemead homes including waterproofing, anti-mold treatments, and surface protection.',
      price: 'From R5,000',
      features: ['Waterproofing', 'Mold prevention', 'Surface protection', 'Long-term durability']
    }
  ];

  const testimonials = [
    {
      name: 'Jennifer Smith',
      property: 'Edgemead Family Home',
      rating: 5,
      text: 'Excellent service for our family home. The team was considerate of our children and pets, worked efficiently, and delivered beautiful results that we absolutely love.',
      image: 'https://public.readdy.ai/ai/img_res/4d321ca2b155086d54161a5b263925fa.jpg'
    },
    {
      name: 'Michael Brown',
      property: 'Edgemead Townhouse',
      rating: 5,
      text: 'Professional and reliable service. They transformed our townhouse with a modern color scheme and the quality of work is outstanding. Highly recommend for Edgemead residents.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20man%20homeowner%20in%20Edgemead%20townhouse%20with%20modern%20painted%20interior%2C%20suburban%20setting%2C%20satisfied%20customer%20portrait&width=80&height=80&seq=edgemead2&orientation=squarish'
    },
    {
      name: 'Sarah Wilson',
      property: 'Edgemead Cottage',
      rating: 5,
      text: 'Wonderful experience from start to finish. The painters were courteous, clean, and delivered exactly what we wanted for our cottage. Great value for money.',
      image: 'https://readdy.ai/api/search-image?query=Friendly%20woman%20homeowner%20in%20charming%20Edgemead%20cottage%20with%20freshly%20painted%20walls%2C%20cozy%20suburban%20home%2C%20satisfied%20client&width=80&height=80&seq=edgemead3&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Modern Family Home',
      description: 'Complete interior and exterior renovation of contemporary Edgemead family home with modern color schemes and child-safe materials',
      image: 'https://readdy.ai/api/search-image?query=Modern%20family%20home%20in%20Edgemead%20with%20freshly%20painted%20exterior%2C%20suburban%20setting%2C%20contemporary%20residential%20architecture%2C%20professional%20painting%20work&width=400&height=300&seq=edgemead4&orientation=landscape'
    },
    {
      title: 'Suburban Townhouse Complex',
      description: 'Comprehensive painting project for Edgemead townhouse development including exterior facades and common areas',
      image: 'https://readdy.ai/api/search-image?query=Townhouse%20complex%20in%20Edgemead%20with%20professionally%20painted%20exteriors%2C%20suburban%20development%2C%20residential%20community%20setting&width=400&height=300&seq=edgemead5&orientation=landscape'
    },
    {
      title: 'Family Kitchen Renovation',
      description: 'Interior painting transformation of family kitchen and dining area with modern colors and durable finishes',
      image: 'https://readdy.ai/api/search-image?query=Modern%20family%20kitchen%20in%20Edgemead%20home%20with%20freshly%20painted%20walls%2C%20contemporary%20design%2C%20family-friendly%20space%2C%20professional%20interior%20painting&width=400&height=300&seq=edgemead6&orientation=landscape'
    },
    {
      title: 'Cottage Style Home',
      description: 'Charming cottage painting project with traditional colors and techniques that enhance the home\'s character',
      image: 'https://readdy.ai/api/search-image?query=Charming%20cottage%20in%20Edgemead%20with%20traditional%20painted%20exterior%2C%20suburban%20garden%20setting%2C%20cozy%20residential%20architecture&width=400&height=300&seq=edgemead7&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20family%20home%20in%20Edgemead%20suburban%20neighborhood%2C%20residential%20painting%20project%2C%20painting%20equipment%2C%20family-friendly%20environment&width=1920&height=1080&seq=edgemeadhero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Edgemead
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Edgemead family homes and residential properties. Specialized solutions for suburban living with child-safe materials, family-friendly scheduling, and quality finishes that enhance your home's comfort and value.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Edgemead Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Painting Excellence in Edgemead
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Edgemead is a thriving suburban community in Cape Town's northern suburbs, known for its family-friendly environment, well-maintained homes, and strong sense of community. Our painting services are specifically tailored to meet the needs of Edgemead families, offering flexible scheduling and child-safe materials.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Located in a peaceful residential area, Edgemead homes range from modern family houses to charming cottages and townhouse developments. We understand the importance of maintaining your home's appearance while minimizing disruption to your family's daily routine, which is why we offer convenient scheduling and efficient service delivery.
              </p>
              <p className="text-lg text-gray-600">
                Our experience with Edgemead's residential properties ensures we deliver quality results that enhance your home's comfort and value while respecting the family-oriented nature of this wonderful community.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Edgemead%20suburban%20neighborhood%20with%20family%20homes%2C%20tree-lined%20streets%2C%20residential%20community%2C%20well-maintained%20properties%2C%20Cape%20Town%20northern%20suburbs&width=600&height=400&seq=edgemead8&orientation=landscape"
                alt="Edgemead Neighborhood"
                className="w-full h-96 object-cover object-center rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Edgemead Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Family-focused painting solutions designed for Edgemead's residential community and suburban lifestyle.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Edgemead Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in family home painting with recent successful projects in Edgemead.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-center"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Edgemead Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Edgemead families who have experienced our exceptional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Edgemead Home?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's family-friendly painting specialists for Edgemead. Our expert team is ready to provide you with quality painting solutions that work around your family's schedule and needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
